import { CopyOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import {
  DatePicker,
  Drawer,
  Select,
  Skeleton,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useRolodexEntries } from 'features/rolodex'
import isNil from 'lodash/isNil'
import type { FC } from 'react'
import {
  lazy,
  Suspense,
  useCallback,
  useDeferredValue,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'
import { displayNameFromEmail } from 'utils/stringHelpers'

import { copyTicketId, TicketTitleBtn } from './copyTicketId'

const TicketLlmStatsTab = lazy(
  () => import('./ticketLlmStats/TicketLlmStatsTab')
)

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RawTicketEntry {
  id: string
  title: string
  createdAt: string
  updatedAt: string
  createdBy: string | undefined
  lastUpdatedBy: string | undefined
  site: string | undefined
}

interface PersonStat {
  /** Raw email / identifier used as the lookup key */
  rawKey: string
  person: string
  created: number
  updated: number
  lastActive: string
}

interface TicketActivityRow {
  key: string
  ticketId: string
  ticket: string
  action: 'created' | 'updated'
  createdBy: string | undefined
  createdAt: string
  lastUpdatedBy: string | undefined
  updatedAt: string
  /** The timestamp most relevant for this action */
  timestamp: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatTimestamp = (iso: string): string => {
  if (!iso) return '—'
  return dayjs(iso).format('MMM D, YYYY h:mm A')
}

const isWithinRange = (iso: string, range: [Dayjs, Dayjs] | null): boolean => {
  if (!range) return true
  const ts = dayjs(iso)
  return (
    ts.isAfter(range[0].startOf('day')) && ts.isBefore(range[1].endOf('day'))
  )
}

// Build columns for the inner expanded table, deriving user filter lists from
// the actual rows so every filter option is grounded in real data.
const buildTicketActivityColumns = (
  rows: TicketActivityRow[]
): ColumnsType<TicketActivityRow> => {
  const uniqueCreators = [
    ...new Set(rows.map((r) => r.createdBy).filter(Boolean)),
  ] as string[]
  const uniqueUpdaters = [
    ...new Set(rows.map((r) => r.lastUpdatedBy).filter(Boolean)),
  ] as string[]

  return [
    {
      title: 'Ticket',
      dataIndex: 'ticket',
      key: 'ticket',
      ellipsis: true,
      render: (title: string, record) => (
        <Tooltip title={`Copy ID: ${record.ticketId}`} placement='topLeft'>
          <TicketTitleBtn onClick={() => copyTicketId(record.ticketId)}>
            {title}
            <CopyOutlined />
          </TicketTitleBtn>
        </Tooltip>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      filters: [
        { text: 'Created', value: 'created' },
        { text: 'Updated', value: 'updated' },
      ],
      onFilter: (value, record) => record.action === value,
      render: (v: TicketActivityRow['action']) => {
        const cfg = {
          created: { color: 'green', label: 'Created' },
          updated: { color: 'gold', label: 'Updated' },
        } as const
        return <Tag color={cfg[v].color}>{cfg[v].label}</Tag>
      },
    },
    {
      title: 'Created',
      key: 'createdInfo',
      width: 220,
      sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
      filters: uniqueCreators.map((raw) => ({
        text: displayNameFromEmail(raw),
        value: raw,
      })),
      onFilter: (value, record) => record.createdBy === value,
      render: (_: unknown, record) => (
        <UserTimeCell>
          <span>{displayNameFromEmail(record.createdBy)}</span>
          <Timestamp>{formatTimestamp(record.createdAt)}</Timestamp>
        </UserTimeCell>
      ),
    },
    {
      title: 'Last Updated',
      key: 'updatedInfo',
      width: 220,
      sorter: (a, b) => b.updatedAt.localeCompare(a.updatedAt),
      defaultSortOrder: 'descend',
      filters: uniqueUpdaters.map((raw) => ({
        text: displayNameFromEmail(raw),
        value: raw,
      })),
      onFilter: (value, record) => record.lastUpdatedBy === value,
      render: (_: unknown, record) => (
        <UserTimeCell>
          <span>{displayNameFromEmail(record.lastUpdatedBy)}</span>
          <Timestamp>{formatTimestamp(record.updatedAt)}</Timestamp>
        </UserTimeCell>
      ),
    },
  ]
}

// ---------------------------------------------------------------------------
// Outer table columns (module-scope — has no state dependencies)
// ---------------------------------------------------------------------------

const personColumns: ColumnsType<PersonStat> = [
  {
    title: 'Person',
    dataIndex: 'person',
    key: 'person',
    render: (v: string) => <PersonLabel>{v}</PersonLabel>,
  },
  {
    title: 'Created',
    dataIndex: 'created',
    key: 'created',
    align: 'center',
    sorter: (a, b) => a.created - b.created,
    render: (v: number) => (
      <StatCell $color='#52c41a'>
        <PlusOutlined />
        {v}
      </StatCell>
    ),
  },
  {
    title: 'Updated',
    dataIndex: 'updated',
    key: 'updated',
    align: 'center',
    sorter: (a, b) => a.updated - b.updated,
    render: (v: number) => (
      <StatCell $color='#faad14'>
        <EditOutlined />
        {v}
      </StatCell>
    ),
  },
  {
    title: 'Last Active',
    dataIndex: 'lastActive',
    key: 'lastActive',
    sorter: (a, b) => b.lastActive.localeCompare(a.lastActive),
    render: (v: string) => (
      <Timestamp>{v ? formatTimestamp(v) : '—'}</Timestamp>
    ),
  },
]

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type Props = {
  orgId: string
  groupId?: string
  onClose: () => void
}

export const InternalGeckoStatsDrawer: FC<Props> = ({
  orgId,
  groupId,
  onClose,
}) => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [siteFilter, setSiteFilter] = useState<string | undefined>(undefined)
  const deferredSiteFilter = useDeferredValue(siteFilter)

  const { entries, isLoading } = useRolodexEntries(
    useMemo(
      () => ({
        type: RolodexEntryType.LIBRA_TICKET,
        // Filter by the active group when available — same as the dashboard
        // content view. Falls back to org-wide if no group is selected.
        tags: groupId
          ? ({ [RolodexEntryType.LIBRA_TICKET_GROUP]: [groupId] } as Record<
              string,
              string[]
            >)
          : { organization: orgId },
      }),
      [orgId, groupId]
    )
  )

  // Map raw entries to a flat structure we can work with
  const rawTickets = useMemo<RawTicketEntry[]>(() => {
    return (entries ?? [])
      .filter((e) => e.type === RolodexEntryType.LIBRA_TICKET)
      .map((e) => ({
        id: e.id,
        title: (e.data.title as string | undefined) ?? e.id,
        createdAt: e.raw.created_at ?? '',
        updatedAt: e.updated_at,
        createdBy: e.raw.created_by,
        lastUpdatedBy: e.raw.last_updated_by,
        site: (e.tags as Record<string, string>).site,
      }))
  }, [entries])

  const siteOptions = useMemo(
    () =>
      [
        ...new Set(
          rawTickets.map((t) => t.site).filter((s): s is string => !isNil(s))
        ),
      ].sort(),
    [rawTickets]
  )

  // Apply date-range and site filters
  const filteredTickets = useMemo<RawTicketEntry[]>(() => {
    return rawTickets.filter((t) => {
      if (!isNil(deferredSiteFilter) && t.site !== deferredSiteFilter)
        return false
      if (!dateRange) return true
      return (
        isWithinRange(t.createdAt, dateRange) ||
        isWithinRange(t.updatedAt, dateRange)
      )
    })
  }, [rawTickets, dateRange, deferredSiteFilter])

  // ── Per-person summary + per-person activity map ─────────────────────────
  const { personStats, activityByPerson } = useMemo(() => {
    const statsMap = new Map<string, PersonStat>()
    const actMap = new Map<string, TicketActivityRow[]>()

    const getOrCreateStat = (rawKey: string): PersonStat => {
      if (!statsMap.has(rawKey)) {
        statsMap.set(rawKey, {
          rawKey,
          person: displayNameFromEmail(
            rawKey === '__unknown__' ? undefined : rawKey
          ),
          created: 0,
          updated: 0,
          lastActive: '',
        })
      }
      return statsMap.get(rawKey)!
    }

    const pushActivity = (rawKey: string, row: TicketActivityRow) => {
      if (!actMap.has(rawKey)) actMap.set(rawKey, [])
      actMap.get(rawKey)!.push(row)
    }

    const bump = (stat: PersonStat, ts: string) => {
      if (!stat.lastActive || ts > stat.lastActive) stat.lastActive = ts
    }

    filteredTickets.forEach((t) => {
      const creatorKey = t.createdBy ?? '__unknown__'
      const updaterKey = t.lastUpdatedBy ?? '__unknown__'

      // ── Creation event ──────────────────────────────────────────────────
      // Independent of update — goes into the creator's rows only
      if (t.createdAt && isWithinRange(t.createdAt, dateRange)) {
        const s = getOrCreateStat(creatorKey)
        s.created++
        bump(s, t.createdAt)
        pushActivity(creatorKey, {
          key: `${t.id}-created`,
          ticketId: t.id,
          ticket: t.title,
          action: 'created',
          createdBy: t.createdBy,
          createdAt: t.createdAt,
          lastUpdatedBy: t.lastUpdatedBy,
          updatedAt: t.updatedAt,
          timestamp: t.createdAt,
        })
      }

      // ── Update / completion event ───────────────────────────────────────
      // Independent of creation — goes into the updater's rows only.
      // Skip only when it's the same person AND timestamps are identical,
      // which just means Rolodex stamped updated_at = created_at on creation
      // (i.e. nothing was actually updated yet).
      const isInitialCreationOnly =
        creatorKey === updaterKey && t.createdAt === t.updatedAt

      if (!isInitialCreationOnly && isWithinRange(t.updatedAt, dateRange)) {
        const s = getOrCreateStat(updaterKey)
        s.updated++
        bump(s, t.updatedAt)
        pushActivity(updaterKey, {
          key: `${t.id}-updated`,
          ticketId: t.id,
          ticket: t.title,
          action: 'updated',
          createdBy: t.createdBy,
          createdAt: t.createdAt,
          lastUpdatedBy: t.lastUpdatedBy,
          updatedAt: t.updatedAt,
          timestamp: t.updatedAt,
        })
      }
    })

    const stats = [...statsMap.values()].sort((a, b) =>
      b.lastActive.localeCompare(a.lastActive)
    )

    // Sort each person's activity rows newest-first
    actMap.forEach((rows) =>
      rows.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
    )

    return { personStats: stats, activityByPerson: actMap }
  }, [filteredTickets, dateRange])

  // ── Summary numbers ──────────────────────────────────────────────────────
  const totalTickets = filteredTickets.length
  const activeContributors = personStats.length
  const externallyAuthoredTickets = filteredTickets.reduce(
    (sum, ticket) =>
      sum +
      (!ticket.createdBy?.includes('@geckorobotics.com') ||
      !ticket.lastUpdatedBy?.includes('@geckorobotics.com')
        ? 1
        : 0),
    0
  )

  // ── Expanded row renderer ────────────────────────────────────────────────
  // Memoised so the outer `teamActivityTab` useMemo has a stable dep — the
  // closure only reads `activityByPerson`, which is already memoised above.
  const expandedRowRender = useCallback(
    (record: PersonStat) => {
      const rows = activityByPerson.get(record.rawKey) ?? []
      return (
        <ExpandedTableWrapper>
          <Table<TicketActivityRow>
            dataSource={rows}
            columns={buildTicketActivityColumns(rows)}
            rowKey='key'
            size='small'
            pagination={
              rows.length > 10 ? { pageSize: 10, size: 'small' } : false
            }
            locale={{ emptyText: 'No ticket activity found' }}
          />
        </ExpandedTableWrapper>
      )
    },
    [activityByPerson]
  )

  const teamActivityTab = useMemo(
    () => (
      <>
        <FilterRow>
          <FilterLabel>Site:</FilterLabel>
          <Select<string | undefined>
            placeholder='All sites'
            value={siteFilter}
            onChange={setSiteFilter}
            allowClear
            style={{ minWidth: 220 }}
            options={siteOptions.map((s) => ({ value: s, label: s }))}
          />
          <FilterLabel>Date range:</FilterLabel>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(val) => setDateRange(val as [Dayjs, Dayjs] | null)}
            allowClear
            placeholder={['Start date', 'End date']}
          />
        </FilterRow>

        {isLoading ? (
          <Skeleton active paragraph={{ rows: 2 }} />
        ) : (
          <SummaryRow>
            <SummaryCard>
              <SummaryValue>{totalTickets}</SummaryValue>
              <SummaryLabel>Total Tickets</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>{externallyAuthoredTickets}</SummaryValue>
              <SummaryLabel> Externally Authored</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue $color='#52c41a'>{activeContributors}</SummaryValue>
              <SummaryLabel>Contributors</SummaryLabel>
            </SummaryCard>
          </SummaryRow>
        )}

        <SectionTitle>Per-Person Breakdown</SectionTitle>
        <Table<PersonStat>
          dataSource={personStats}
          columns={personColumns}
          rowKey='rawKey'
          size='small'
          pagination={false}
          loading={isLoading}
          locale={{ emptyText: 'No activity in this range' }}
          expandable={{
            expandedRowRender,
            rowExpandable: (record) =>
              (activityByPerson.get(record.rawKey)?.length ?? 0) > 0,
          }}
        />
      </>
    ),
    [
      dateRange,
      siteFilter,
      setSiteFilter,
      siteOptions,
      isLoading,
      totalTickets,
      externallyAuthoredTickets,
      activeContributors,
      personStats,
      expandedRowRender,
      activityByPerson,
    ]
  )

  const ticketMetricsTab = useMemo(
    () => (
      <Suspense fallback={<Skeleton active paragraph={{ rows: 4 }} />}>
        <TicketLlmStatsTab orgId={orgId} groupId={groupId} />
      </Suspense>
    ),
    [orgId, groupId]
  )

  const tabItems = useMemo(
    () => [
      {
        key: 'team-activity',
        label: 'Team Activity',
        children: teamActivityTab,
      },
      {
        key: 'ticket-metrics',
        label: 'Ticket Metrics',
        children: ticketMetricsTab,
      },
    ],
    [teamActivityTab, ticketMetricsTab]
  )

  return (
    <Drawer
      title='Internal Stats'
      placement='right'
      width={960}
      onClose={onClose}
      open
      styles={{ body: { padding: '1.25rem 1.5rem', overflowY: 'auto' } }}>
      <Tabs defaultActiveKey='team-activity' items={tabItems} />
    </Drawer>
  )
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`

const FilterLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
  white-space: nowrap;
`

const SummaryRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`

const SummaryCard = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.gray[800]};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 8px;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const SummaryValue = styled.div<{ $color?: string }>`
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  color: ${({ $color, theme }) => $color ?? theme.forecast.activeText};
`

const SummaryLabel = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const SectionTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
`

const PersonLabel = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
`

const StatCell = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${({ $color }) => $color};
  font-weight: 600;
`

const Timestamp = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const ExpandedTableWrapper = styled.div`
  padding: 0.5rem 1rem 0.75rem 2.5rem;
  background: ${({ theme }) => theme.colors.gray[800]};
`

const UserTimeCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  line-height: 1.3;

  > span:first-child {
    font-weight: 500;
    color: ${({ theme }) => theme.forecast.activeText};
    font-size: 0.8125rem;
  }
`
